import React, { useCallback, useState } from "react";
import apiFetch, { safeJson } from "../../../context/api";

/**
 * "Enviar a Barra" (CRUO F1): convierte el carrito de productos del socio
 * seleccionado en una COMANDA que aparece en la pantalla de Barra.
 *
 * Puente por API sin tocar el POS (estrangulamiento, ARQUITECTURA_CARRITO_V2):
 *   1. Lee el draft viejo del embed (GET /v1/draft-cart, cart_type=productos)
 *   2. Lo vuelca a Cart v2 (POST /v1/cart, context pos_productos)
 *   3. Lo convierte en comanda (POST /v1/cart/checkout-to-order)
 *   4. Vacía el draft del embed (DELETE /v1/draft-cart)
 * Sin mesa (llega con service_tables): la comanda nace "recoge en barra".
 */
export default function EnviarABarra({ token, venueID, socioId, socioName, onSent }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // {tone: 'ok'|'warn'|'err', text}

  const enviar = useCallback(async () => {
    if (!token || !venueID || !socioId || busy) return;
    setBusy(true);
    setMsg(null);
    const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
    try {
      // 1) carrito actual del socio en el embed (draft viejo)
      const draftRes = await apiFetch(
        `/v1/draft-cart?user_id=${socioId}&venue_id=${venueID}&cart_type=productos`,
        { headers }
      );
      if (!draftRes.ok) throw new Error("No se pudo leer el carrito del socio");
      const draft = await safeJson(draftRes);
      const items = (draft?.items || []).filter((it) => Number(it.quantity) > 0);
      if (items.length === 0) {
        setMsg({ tone: "warn", text: "El carrito está vacío — añade productos antes de enviar." });
        return;
      }

      // 2) volcar a Cart v2
      const cartBody = {
        venue_id: venueID,
        owner_user_id: socioId,
        context: "pos_productos",
        items: items.map((it) => ({
          product_id: it.product_id,
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price ?? it.price ?? 0),
          location_id: it.location_id || null,
        })),
      };
      const cartRes = await apiFetch(`/v1/cart`, { method: "POST", headers, body: JSON.stringify(cartBody) });
      if (!cartRes.ok) {
        const detail = (await cartRes.json().catch(() => null))?.detail;
        throw new Error(typeof detail === "string" ? detail : "No se pudo preparar la comanda");
      }

      // 3) carrito → comanda
      const orderRes = await apiFetch(`/v1/cart/checkout-to-order`, {
        method: "POST",
        headers,
        body: JSON.stringify({ venue_id: venueID, owner_user_id: socioId, context: "pos_productos" }),
      });
      if (!orderRes.ok) {
        const detail = (await orderRes.json().catch(() => null))?.detail;
        throw new Error(typeof detail === "string" ? detail : "No se pudo crear la comanda");
      }
      const order = await safeJson(orderRes);

      // 4) vaciar el draft del embed (la comanda ya vive en Barra)
      await apiFetch(
        `/v1/draft-cart?user_id=${socioId}&venue_id=${venueID}&cart_type=productos`,
        { method: "DELETE", headers }
      ).catch(() => {});

      setMsg({
        tone: "ok",
        text: `Comanda enviada a Barra ✓ (${order?.fulfillment === "serve" ? "servir en mesa" : "recoge en barra"})`,
      });
      if (onSent) onSent(order);
    } catch (e) {
      setMsg({ tone: "err", text: e.message || "Error enviando la comanda" });
    } finally {
      setBusy(false);
    }
  }, [token, venueID, socioId, busy, onSent]);

  const toneClass =
    msg?.tone === "ok"
      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
      : msg?.tone === "warn"
      ? "text-amber-700 bg-amber-50 border-amber-200"
      : "text-rose-700 bg-rose-50 border-rose-200";

  return (
    <div className="flex flex-col items-end gap-2 p-3">
      <button
        type="button"
        disabled={busy || !socioId}
        onClick={enviar}
        className="rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-gray-300"
      >
        {busy ? "Enviando…" : `🍹 Enviar a Barra${socioName ? ` (${socioName})` : ""}`}
      </button>
      {msg && (
        <div className={`rounded-lg border px-3 py-1.5 text-xs ${toneClass}`} role="status">
          {msg.text}
        </div>
      )}
    </div>
  );
}
